import {
    ApiSocialFriendInviteAcceptReqSchema,
    ApiSocialFriendInviteAcceptResSchema,
    ApiSocialFriendInviteDeclineReqSchema,
    ApiSocialFriendInviteDeclineResSchema,
    ApiSocialFriendInviteRevokeReqSchema,
    ApiSocialFriendInviteRevokeResSchema,
    ApiSocialFriendInvitesResSchema,
    ApiSocialFriendRemoveReqSchema,
    ApiSocialFriendRemoveResSchema,
    ApiSocialFriendsResSchema,
    type ApiSocialFriend,
    type ApiSocialFriendInvite,
} from "@bridge-applications/pb-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { authenticatedApiCall } from "../helpers/api";
import i18n from "../i18n/i18n";

const PAGE_SIZE = 100;

class FriendsApiError extends Error {
    constructor(code: string, operation: "load" | "manage") {
        super(i18n.t(`friends.errors.${operation}`, { code }));
        this.name = "FriendsApiError";
    }
}

function mergeUnique<T>(
    current: T[],
    incoming: T[],
    identity: (value: T) => string,
) {
    const ids = new Set(current.map(identity));
    return [...current, ...incoming.filter((value) => !ids.has(identity(value)))];
}

export function useFriendsManagement(enabled: boolean) {
    const [friends, setFriends] = useState<ApiSocialFriend[]>([]);
    const [invites, setInvites] = useState<ApiSocialFriendInvite[]>([]);
    const [status, setStatus] = useState<
        "idle" | "loading" | "ready" | "error"
    >("idle");
    const [error, setError] = useState<string | null>(null);
    const [friendCursor, setFriendCursor] = useState<string | null>(null);
    const [inviteCursor, setInviteCursor] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState<
        "friends" | "invites" | null
    >(null);
    const [mutatingId, setMutatingId] = useState<string | null>(null);
    const requestRef = useRef<AbortController | null>(null);

    const fetchFriends = useCallback(
        async (cursor?: string, signal?: AbortSignal) => {
            const query = new URLSearchParams({ limit: PAGE_SIZE.toString() });
            if (cursor) query.set("cursor", cursor);
            const { body } = await authenticatedApiCall(
                `/api/v1/social/friends?${query.toString()}`,
                ApiSocialFriendsResSchema,
                { signal },
            );
            if (body.status === "ERROR") {
                throw new FriendsApiError(body.code, "load");
            }
            return body;
        },
        [],
    );

    const fetchInvites = useCallback(
        async (cursor?: string, signal?: AbortSignal) => {
            const query = new URLSearchParams({
                limit: PAGE_SIZE.toString(),
                state: "PENDING",
            });
            if (cursor) query.set("cursor", cursor);
            const { body } = await authenticatedApiCall(
                `/api/v1/social/friend-invites?${query.toString()}`,
                ApiSocialFriendInvitesResSchema,
                { signal },
            );
            if (body.status === "ERROR") {
                throw new FriendsApiError(body.code, "load");
            }
            return body;
        },
        [],
    );

    const refresh = useCallback(async () => {
        if (!enabled) return;
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        setStatus("loading");
        setError(null);
        try {
            const [friendPage, invitePage] = await Promise.all([
                fetchFriends(undefined, controller.signal),
                fetchInvites(undefined, controller.signal),
            ]);
            if (requestRef.current !== controller) return;
            setFriends(friendPage.friends);
            setInvites(invitePage.invites);
            setFriendCursor(friendPage.nextCursor);
            setInviteCursor(invitePage.nextCursor);
            setStatus("ready");
        } catch (loadError) {
            if (controller.signal.aborted) return;
            setStatus("error");
            setError(
                loadError instanceof Error
                    ? loadError.message
                    : i18n.t("errors.generic"),
            );
        } finally {
            if (requestRef.current === controller) requestRef.current = null;
        }
    }, [enabled, fetchFriends, fetchInvites]);

    useEffect(() => {
        if (enabled) void refresh();
        return () => requestRef.current?.abort();
    }, [enabled, refresh]);

    const loadMoreFriends = useCallback(async () => {
        if (!friendCursor || loadingMore) return;
        setLoadingMore("friends");
        setError(null);
        try {
            const page = await fetchFriends(friendCursor);
            setFriends((current) =>
                mergeUnique(current, page.friends, (friend) =>
                    friend.friendshipId,
                ),
            );
            setFriendCursor(page.nextCursor);
        } catch (loadError) {
            setError(
                loadError instanceof Error
                    ? loadError.message
                    : i18n.t("errors.generic"),
            );
        } finally {
            setLoadingMore(null);
        }
    }, [fetchFriends, friendCursor, loadingMore]);

    const loadMoreInvites = useCallback(async () => {
        if (!inviteCursor || loadingMore) return;
        setLoadingMore("invites");
        setError(null);
        try {
            const page = await fetchInvites(inviteCursor);
            setInvites((current) =>
                mergeUnique(current, page.invites, (invite) => invite.inviteId),
            );
            setInviteCursor(page.nextCursor);
        } catch (loadError) {
            setError(
                loadError instanceof Error
                    ? loadError.message
                    : i18n.t("errors.generic"),
            );
        } finally {
            setLoadingMore(null);
        }
    }, [fetchInvites, inviteCursor, loadingMore]);

    const acceptInvite = useCallback(async (inviteId: string) => {
        const mutationId = `accept:${inviteId}`;
        setMutatingId(mutationId);
        setError(null);
        try {
            const request = ApiSocialFriendInviteAcceptReqSchema.parse({
                inviteId,
            });
            const { body } = await authenticatedApiCall(
                "/api/v1/social/friend-invites/accept",
                ApiSocialFriendInviteAcceptResSchema,
                { method: "POST", body: JSON.stringify(request) },
            );
            if (body.status === "ERROR") {
                throw new FriendsApiError(body.code, "manage");
            }
            setInvites((current) =>
                current.filter((invite) => invite.inviteId !== inviteId),
            );
            const acceptedFriend = body.friend;
            if (acceptedFriend) {
                setFriends((current) => [
                    acceptedFriend,
                    ...current.filter(
                        (friend) =>
                            friend.friendshipId !==
                            acceptedFriend.friendshipId,
                    ),
                ]);
            }
            return true;
        } catch (mutationError) {
            setError(
                mutationError instanceof Error
                    ? mutationError.message
                    : i18n.t("errors.generic"),
            );
            return false;
        } finally {
            setMutatingId(null);
        }
    }, []);

    const declineInvite = useCallback(async (inviteId: string) => {
        const mutationId = `decline:${inviteId}`;
        setMutatingId(mutationId);
        setError(null);
        try {
            const request = ApiSocialFriendInviteDeclineReqSchema.parse({
                inviteId,
            });
            const { body } = await authenticatedApiCall(
                "/api/v1/social/friend-invites/decline",
                ApiSocialFriendInviteDeclineResSchema,
                { method: "POST", body: JSON.stringify(request) },
            );
            if (body.status === "ERROR") {
                throw new FriendsApiError(body.code, "manage");
            }
            setInvites((current) =>
                current.filter((invite) => invite.inviteId !== inviteId),
            );
            return true;
        } catch (mutationError) {
            setError(
                mutationError instanceof Error
                    ? mutationError.message
                    : i18n.t("errors.generic"),
            );
            return false;
        } finally {
            setMutatingId(null);
        }
    }, []);

    const revokeInvite = useCallback(async (inviteId: string) => {
        const mutationId = `revoke:${inviteId}`;
        setMutatingId(mutationId);
        setError(null);
        try {
            const request = ApiSocialFriendInviteRevokeReqSchema.parse({
                inviteId,
            });
            const { body } = await authenticatedApiCall(
                "/api/v1/social/friend-invites/revoke",
                ApiSocialFriendInviteRevokeResSchema,
                { method: "POST", body: JSON.stringify(request) },
            );
            if (body.status === "ERROR") {
                throw new FriendsApiError(body.code, "manage");
            }
            setInvites((current) =>
                current.filter((invite) => invite.inviteId !== inviteId),
            );
            return true;
        } catch (mutationError) {
            setError(
                mutationError instanceof Error
                    ? mutationError.message
                    : i18n.t("errors.generic"),
            );
            return false;
        } finally {
            setMutatingId(null);
        }
    }, []);

    const removeFriend = useCallback(async (friendUserId: string) => {
        const mutationId = `remove:${friendUserId}`;
        setMutatingId(mutationId);
        setError(null);
        try {
            const request = ApiSocialFriendRemoveReqSchema.parse({
                friendUserId,
            });
            const { body } = await authenticatedApiCall(
                "/api/v1/social/friends/remove",
                ApiSocialFriendRemoveResSchema,
                { method: "POST", body: JSON.stringify(request) },
            );
            if (body.status === "ERROR") {
                throw new FriendsApiError(body.code, "manage");
            }
            setFriends((current) =>
                current.filter(
                    (friend) => friend.user.userId !== friendUserId,
                ),
            );
            return true;
        } catch (mutationError) {
            setError(
                mutationError instanceof Error
                    ? mutationError.message
                    : i18n.t("errors.generic"),
            );
            return false;
        } finally {
            setMutatingId(null);
        }
    }, []);

    return {
        friends,
        invites,
        status,
        error,
        friendCursor,
        inviteCursor,
        loadingMore,
        mutatingId,
        refresh,
        loadMoreFriends,
        loadMoreInvites,
        acceptInvite,
        declineInvite,
        revokeInvite,
        removeFriend,
    };
}
